import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDimensions } from '@libs/hooks';
import { useOutsideClick } from '@libs/hooks';
import { screenSizes } from '@libs/theme';
import { filterData } from '@libs/helpers';
import { DropdownIcon } from '../../assets';
import { useModal } from '../../modal';
import { SelectOption } from './SelectModalOption';
import { Separator, ValueText, Placeholder, IconWrapper, CustomSelect, ModalWrapper, Img, SelectIconWrapper, ModalContent, ModalOptionsWrapper, CustomValueWrapper, ValueWrapper, CustomEmptyState, } from './styles';
import { SelectList } from './SelectList';
const SelectModal = function ({ options = [], disabled, separator, placeholder, textAfterSelection, errorMessage, headerButtonText, onHeaderButtonClick, selectedValue, selectedValues, valueSize = 'lg', renderOptionHeader, renderOptionFooter, renderOption, renderValue, className, onChange, shouldCloseOnSelection = true, onOutsideClick, searchInputValue, forceMenuOpen = false, onClose, title, searchKeyWord = ['label', 'description'], JustifyToRight, testId, emptyStateTitle, emptyStateImage, optionFontFamily, modalView, }) {
    const selectRef = useRef(null);
    const { width } = useDimensions();
    const [isOpen, setIsOpen] = useState(false);
    const { openModal, closeModal, Dialog, isOpen: isModalOpen } = useModal();
    const toggleSelect = useCallback(() => {
        if ((options.length <= 1 && !forceMenuOpen) || disabled)
            return;
        if (isModalOpen) {
            closeModal();
        }
        else if (width <= screenSizes.tablet || modalView) {
            openModal();
        }
        setIsOpen((prev) => !prev);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [forceMenuOpen, disabled, options, isModalOpen]);
    const closeSelect = useCallback(() => {
        onClose?.();
        setIsOpen(false);
        if (onOutsideClick) {
            onOutsideClick();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onOutsideClick]);
    useOutsideClick(selectRef, closeSelect);
    // NOTE: closing select and modal (for mobile)
    const closeAll = useCallback(() => {
        closeSelect();
        closeModal();
    }, [closeSelect, closeModal]);
    const filteredOptions = useMemo(() => {
        return searchInputValue
            ? filterData(options, searchInputValue, searchKeyWord)
            : options;
    }, [searchInputValue, options, searchKeyWord]);
    useEffect(() => {
        if (shouldCloseOnSelection && selectedValue !== undefined) {
            closeAll();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [closeAll, shouldCloseOnSelection, selectedValue]);
    const defaultRenderItem = useCallback((opt, index) => {
        const isLast = index === options.length - 1;
        const handleChange = () => {
            if (opt.disabled)
                return;
            onChange?.(opt.value, index, opt);
        };
        if (opt.isHidden) {
            return _jsx(_Fragment, {});
        }
        return (_jsxs("li", { children: [_jsx(SelectOption, { className: "select-option", icon: opt.icon, label: opt.label, value: opt.value, onClick: handleChange, isSelected: opt.value === selectedValue, disabled: !!opt?.disabled, description: opt?.description, testId: opt.value, rigthItem: opt.rightItem, fontType: optionFontFamily, valueSize: valueSize }), separator ? (separator) : (_jsx(Separator, { disabled: opt.disabled, isLast: isLast, className: "separator" }))] }, opt.value));
    }, [onChange, options.length, selectedValue, separator, optionFontFamily]);
    const defaultRenderValue = useMemo(() => {
        const selectedItem = options.filter((opt) => selectedValues?.includes(opt.value) || selectedValue === opt.value);
        if (renderValue) {
            return renderValue(selectedItem?.[0], selectedItem);
        }
        return (_jsxs(CustomValueWrapper, { placeholder: placeholder, textAfterSelection: textAfterSelection, className: "select-value__wrapper", disabled: disabled, children: [(!!placeholder || !!textAfterSelection) && (_jsx(_Fragment, { children: _jsx(Placeholder, { type: "subHeadline", fontFamily: "primary", className: "placeHolder", isFocused: selectedValue !== undefined, hasIcon: !!selectedItem?.[0]?.icon, children: textAfterSelection && selectedValue !== undefined
                            ? textAfterSelection
                            : placeholder }) })), selectedItem?.[0]?.icon && (_jsx(SelectIconWrapper, { className: "select-icon", "data-cy": `${selectedValue}-icon`, children: typeof selectedItem?.[0].icon === 'string' ? (_jsx(Img, { src: selectedItem?.[0]?.icon, className: "select-icon__img" })) : (selectedItem?.[0]?.icon) })), _jsx(ValueText, { hasOtherContent: !!placeholder || !!selectedItem?.[0]?.icon || !!textAfterSelection, type: "subHeadline", className: "valueText", testId: testId + '_' + 'selectedItem', children: selectedItem?.[0]?.label })] }));
    }, [
        options,
        renderValue,
        placeholder,
        textAfterSelection,
        disabled,
        selectedValue,
        testId,
        selectedValues,
    ]);
    const headerBtnText = selectedValue !== undefined || selectedValues?.length !== 0
        ? headerButtonText
        : undefined;
    const shouldShowDropdownIcon = (forceMenuOpen || options.length > 1) && !disabled;
    const shouldHavePointer = options.length > 1;
    return (_jsxs(CustomSelect, { ref: selectRef, valueSize: valueSize, onClick: toggleSelect, isDisabled: disabled, className: className, hasError: !!errorMessage, isSelected: selectedValue !== undefined || !!selectedValues?.length, "data-testid": testId, shouldHavePointer: shouldHavePointer, children: [_jsx(ValueWrapper, { className: "valueWrapper", children: defaultRenderValue }), shouldShowDropdownIcon && (_jsx(IconWrapper, { className: "select-dropdown-icon", isOpen: isOpen, children: _jsx(DropdownIcon, {}) })), isOpen && !modalView && (_jsx(SelectList, { options: filteredOptions, closeSelect: closeSelect, JustifyToRight: JustifyToRight, renderOptionHeader: renderOptionHeader, renderOption: renderOption || defaultRenderItem, renderOptionFooter: renderOptionFooter, selectedValues: selectedValues, selectedValue: selectedValue, emptyStateImage: emptyStateImage, emptyStateTitle: emptyStateTitle })), _jsx(ModalWrapper, { children: _jsxs(Dialog, { leftSpace: 0, title: title || placeholder, testId: "select-modal", headerButtonText: headerBtnText, onHeaderButtonClick: onHeaderButtonClick, headerBackground: "transparent", shouldHideHeaderBorder: true, children: [renderOptionHeader && renderOptionHeader(closeSelect), _jsxs(ModalContent, { children: [_jsx(ModalOptionsWrapper, { children: !filteredOptions.length && emptyStateTitle ? (_jsx(CustomEmptyState, { title: emptyStateTitle, img: emptyStateImage })) : (filteredOptions.map(renderOption || defaultRenderItem)) }), renderOptionFooter &&
                                    renderOptionFooter(closeAll, selectedValue, selectedValues)] })] }) })] }));
};
export default SelectModal;
//# sourceMappingURL=SelectModal.js.map